import React, { useState, useEffect } from 'react';
import { Select, Input, Spin } from 'antd';
import { noop } from 'lodash';
import { useTranslation } from 'react-i18next';
import ConfirmModal from 'src/components/confirm-modal';
import { getReasons } from 'src/apis/fitReason';
import './style.scss';

const { TextArea } = Input;
const { Option, OptGroup } = Select;

/**
 * Modal to select a reason before undoing a fit.
 * Categories appear as bold non-selectable group headers; subcategories are selectable.
 *
 * @param {Object} props
 * @param {Function} props.onCancel
 * @param {Function} props.onConfirm - called with (cancelCategory, cancelSubcategory)
 * @param {boolean} props.loading
 */
function UndoFitReasonModal({ onCancel, onConfirm, loading }) {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language === 'en' || i18n.language?.startsWith('en');

  const [categories, setCategories] = useState([]);
  const [fetchingReasons, setFetchingReasons] = useState(true);
  const [selectedValue, setSelectedValue] = useState(null);
  const [selectedMeta, setSelectedMeta] = useState(null);
  const [otherText, setOtherText] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    getReasons('UNDO_FIT')
      .then(response => setCategories(response?.message || []))
      .catch(() => setCategories([]))
      .finally(() => setFetchingReasons(false));
  }, []);

  const handleSelect = (value, option) => {
    setSelectedValue(value);
    setSelectedMeta({ categoryKey: option.categoryKey, isOther: option.isOther });
    setOtherText('');
    setError(null);
  };

  const handleConfirm = () => {
    if (!selectedMeta) {
      setError(t('undoFitReasonRequired'));
      return;
    }
    if (selectedMeta.isOther && otherText.trim().length < 3) {
      setError(t('undoFitOtherReasonRequired'));
      return;
    }
    onConfirm(selectedMeta.categoryKey, selectedMeta.isOther ? otherText.trim() : selectedValue);
  };

  return (
    <ConfirmModal
      onCancel={onCancel}
      onConfirm={handleConfirm}
      okText={t('confirm')}
      cancelText={t('cancel')}
      title={t('confirmUndoFit')}
      visible={true}
      className='undo-fit-reason-modal'
      confirmLoading={loading}
    >
      <div className='undo-fit-reason-modal__content'>
        <div className='undo-fit-reason-modal__field'>
          <label className='undo-fit-reason-modal__label'>
            {t('selectTheReason')}*
          </label>
          {fetchingReasons ? (
            <Spin />
          ) : (
            <Select
              style={{ width: '100%' }}
              value={selectedValue}
              onChange={handleSelect}
              placeholder={t('selectTheReason')}
            >
              {categories.map(category => (
                <OptGroup
                  key={category.categoryKey}
                  label={
                    <strong>{isEn ? category.categoryEn : category.categoryPtBr}</strong>
                  }
                >
                  {category.subcategories.map(sub => (
                    <Option
                      key={sub.subcategoryKey ?? 'OTHERS'}
                      value={sub.subcategoryKey ?? 'OTHERS'}
                      categoryKey={category.categoryKey}
                      isOther={sub.isOther}
                    >
                      {isEn ? sub.subcategoryEn : sub.subcategoryPtBr}
                    </Option>
                  ))}
                </OptGroup>
              ))}
            </Select>
          )}
        </div>

        {selectedMeta?.isOther && (
          <div className='undo-fit-reason-modal__field'>
            <label className='undo-fit-reason-modal__label'>
              {t('otherReasonLabel')}*
            </label>
            <TextArea
              maxLength={50}
              showCount
              rows={4}
              value={otherText}
              onChange={e => { setOtherText(e.target.value); setError(null); }}
              placeholder={t('undoFitOtherReason')}
            />
          </div>
        )}

        {error && (
          <p className='undo-fit-reason-modal__error' role='alert'>{error}</p>
        )}
      </div>
    </ConfirmModal>
  );
}

UndoFitReasonModal.defaultProps = {
  onCancel: noop,
  onConfirm: noop,
  loading: false,
};

export default UndoFitReasonModal;
